import PageHero from '../components/PageHero.jsx';
import Accordion from '../components/Accordion.jsx';
import BellSchedule from '../components/BellSchedule.jsx';
import InfoIcon from '../components/InfoIcon.jsx';
import { SOURCES } from '../data/sources.js';
import { bellSchedule } from '../data/bellSchedule.js';
import { whatToDoIf } from '../data/rules.js';
import './DayInLife.css';

const glanceSource = {
  ...SOURCES.bellSchedulePdf,
  note: bellSchedule.verificationNote,
  verificationNeeded: true,
};

function telHref(phone) {
  return `tel:+1${phone.replace(/\D/g, '')}`;
}

export default function DayInLife() {
  return (
    <div className="day">
      <PageHero icon="/assets/eagle/page-icons/day.png" title="A day at Canyon Ridge" />

      {/* ── 1. The day at a glance (always visible) ── */}
      <div className="day-glance-card">
        <div className="day-glance-header">
          <h2 className="day-glance-title">The day at a glance</h2>
          <InfoIcon source={glanceSource} />
        </div>
        <div className="day-glance-grid">
          <div className="day-glance-item">
            <span className="day-glance-label">Doors open</span>
            <span className="day-glance-time">8:35 AM</span>
          </div>
          <div className="day-glance-item">
            <span className="day-glance-label">First bell</span>
            <span className="day-glance-time">9:00 AM</span>
          </div>
          <div className="day-glance-item">
            <span className="day-glance-label">Last bell</span>
            <span className="day-glance-time">4:20 PM</span>
          </div>
          <div className="day-glance-item">
            <span className="day-glance-label">Doors close</span>
            <span className="day-glance-time">4:40 PM</span>
          </div>
          <div className="day-glance-item">
            <span className="day-glance-label">Early release</span>
            <span className="day-glance-time">1:05 PM</span>
          </div>
        </div>
      </div>

      {/* ── Accordions 2–9 ── */}
      <div className="day-accordions">

        {/* 2. Bell schedule */}
        <div className="day-section">
          <Accordion title="Bell schedule">
            <BellSchedule />
          </Accordion>
        </div>

        {/* 3. What to bring */}
        <div className="day-section">
          <Accordion title="What to bring">
            <ul className="accordion-list">
              <li className="accordion-list-item">Backpack</li>
              <li className="accordion-list-item">
                mLISD Chromebook (if reserved via MySchoolBucks)
                <InfoIcon source={SOURCES.mLisd} />
              </li>
              <li className="accordion-list-item">Water bottle</li>
              <li className="accordion-list-item">Lunch (or MySchoolBucks balance for campus purchase)</li>
              <li className="accordion-list-item">Planner</li>
              <li className="accordion-list-item">Pencils and basic supplies</li>
            </ul>
          </Accordion>
        </div>

        {/* 4. Lunch */}
        <div className="day-section">
          <Accordion title="Lunch">
            <ul className="accordion-list">
              <li className="accordion-list-item">
                Brought from home or purchased on campus via{' '}
                <a
                  href="https://www.myschoolbucks.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="accordion-link"
                >
                  MySchoolBucks
                </a>
              </li>
              <li className="accordion-list-item">
                $16 charge limit (~5 lunches) before low-cost meal substitution. Trays are{' '}
                <strong>never taken away</strong>.
              </li>
              <li className="accordion-list-item">
                Reduced-price meals are <strong>FREE for 2026-27</strong> thanks to a state grant from
                the 89th Texas Legislature.
                <InfoIcon source={SOURCES.childNutrition} />
              </li>
              <li className="accordion-list-item">
                6th-grade lunch is <strong>5th period</strong> — the late slot. See the bell schedule for
                exact times.
              </li>
            </ul>
          </Accordion>
        </div>

        {/* 5. Bus + SMART Tag */}
        <div className="day-section">
          <Accordion title="Bus + SMART Tag">
            <ul className="accordion-list">
              <li className="accordion-list-item">
                Annual registration required — transportation registration opened April 1, 2026.
                <InfoIcon source={SOURCES.busInfo} />
              </li>
              <li className="accordion-list-item">
                Tap the SMART Tag card on and off the bus (RFID). Card is issued with registration.
                <InfoIcon source={SOURCES.smartTagPortal} />
              </li>
              <li className="accordion-list-item">
                Parent portal tracks live bus location and sends SMS alerts ~10–15 minutes before arrival.
              </li>
              <li className="accordion-list-item">
                Portal:{' '}
                <a
                  href="https://parent.smart-tag.net/@leanderisd"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="accordion-link"
                >
                  parent.smart-tag.net/@leanderisd
                </a>
              </li>
            </ul>
          </Accordion>
        </div>

        {/* 6. Phones (HB 1481) — rust accent dot flags this as important */}
        <div className="day-section">
          <Accordion title="Phones (HB 1481)" accent="rust">
            <div className="phones-highlight">
              <p className="phones-highlight-text">
                Phones, smartwatches, tablets, and earbuds are prohibited bell-to-bell —
                including lunch and passing periods.
              </p>
            </div>
            <ul className="accordion-list">
              <li className="accordion-list-item">
                Device must be off and out of sight — in your locker or backpack.
                <InfoIcon source={SOURCES.hb1481} />
              </li>
              <li className="accordion-list-item">
                Confiscation on first offense; device returned to a parent or guardian.
              </li>
              <li className="accordion-list-item">
                Repeat violations escalate per the Student Code of Conduct.
              </li>
              <li className="accordion-list-item">
                Exceptions: documented IEP/504 accommodations, medical needs, or
                safety/health requirements imposed by law.
              </li>
            </ul>
          </Accordion>
        </div>

        {/* 7. Lockers */}
        <div className="day-section">
          <Accordion title="Lockers">
            <ul className="accordion-list">
              <li className="accordion-list-item">
                Assigned at orientation with a three-number combination.
                <InfoIcon source={SOURCES.studentHandbook} />
              </li>
              <li className="accordion-list-item">
                Forgot your combination? Go to the front office — they can look it up.
              </li>
              <li className="accordion-list-item">
                Don't share your combination with anyone, including friends.
              </li>
            </ul>
          </Accordion>
        </div>

        {/* 8. Drills */}
        <div className="day-section">
          <Accordion title="Drills">
            <p className="accordion-prose">
              CRMS uses the <strong>Standard Response Protocol</strong> for all campus
              emergencies: Hold, Secure, Lockdown, Evacuate, Shelter.
              <InfoIcon source={SOURCES.studentHandbook} />
            </p>
            <ul className="accordion-list">
              <li className="accordion-list-item">
                Drills happen periodically throughout the year without advance notice.
              </li>
              <li className="accordion-list-item">
                Follow teacher and staff instructions exactly during any drill.
              </li>
              <li className="accordion-list-item">
                Each protocol has specific actions — you'll practice them in drills.
              </li>
            </ul>
          </Accordion>
        </div>

        {/* 9. Tardies */}
        <div className="day-section">
          <Accordion title="Tardies">
            <ul className="accordion-list">
              <li className="accordion-list-item">
                Arriving within the first 10 minutes of class = tardy.
                <InfoIcon source={SOURCES.studentHandbook} />
              </li>
              <li className="accordion-list-item">
                Arriving 10 or more minutes late = <strong>absent for that period</strong>. This
                affects your attendance record.
              </li>
              <li className="accordion-list-item">
                This is a Texas Education Code rule, not a CRMS-specific policy.
              </li>
            </ul>
          </Accordion>
        </div>

      </div>

      {/* ── 10. If something goes wrong (always visible — no accordion) ── */}
      <section aria-labelledby="situations-heading">
        <h2 id="situations-heading" className="day-situations-heading">
          If something goes wrong
        </h2>
        <p className="day-situations-subtext">
          Quick reference — what to do and who to call.
        </p>
        <div className="situation-grid">
          {whatToDoIf.map(item => (
            <div key={item.id} className="situation-card">
              <div className="situation-card-header">
                <h3 className="situation-title">{item.situation}</h3>
                <InfoIcon source={item.source} />
              </div>
              <p className="situation-response">{item.response}</p>
              {(item.contact || item.contactPhone || item.contactUrl) && (
                <div className="situation-contact">
                  {item.contact && (
                    <span className="situation-contact-label">{item.contact}</span>
                  )}
                  {item.contactPhone && (
                    <a href={telHref(item.contactPhone)} className="situation-contact-link">
                      {item.contactPhone}
                    </a>
                  )}
                  {item.contactUrl && (
                    <a
                      href={item.contactUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="situation-contact-link situation-contact-link--web"
                    >
                      website ↗
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
